// Blocks of a long text (decision 034, um/2026-10-30/03 п.2): the text is kept as markdown, the editor works with a list of blocks —
// one markdown line or fence per block. parse → serialize keeps what the reader sees (the round trip test runs on all demo texts).

import { RECORD_HREF } from './inline.ts';

export type BlockKind = 'p' | 'h1' | 'h2' | 'h3' | 'bullet' | 'number' | 'todo' | 'quote' | 'callout' | 'code' | 'divider' | 'file' | 'record';

/**
 * One block. `text` is inline markdown (for `code` — the code as typed). A file has its `url` and `text` is its name;
 * a record link has `recordId` and its title at the moment of linking in `text`.
 */
export interface Block { id: string; kind: BlockKind; text: string; checked?: boolean; lang?: string; url?: string; recordId?: string }

let seq = 0;
/** A block id unique in this page: blocks are not stored with ids, only the editor tells them apart. */
export const blockId = () => `b${Date.now().toString(36)}${(seq++).toString(36)}`;
export const newBlock = (kind: BlockKind = 'p', text = '', more: Partial<Block> = {}): Block => ({ id: blockId(), kind, text, ...more });

const LIST: Partial<Record<BlockKind, string>> = { bullet: 'ul', todo: 'ul', number: 'ol' };
/** Whether two blocks are items of one list: they stay on adjacent lines. */
export const sameList = (a: Block, b: Block) => !!LIST[a.kind] && LIST[a.kind] === LIST[b.kind];

/** A line that starts a block other than a paragraph; such a line inside a paragraph is written with «\». */
const BLOCK_START = /^(#{1,3}\s|[-*+]\s|\d+[.)]\s|>|```|(-{3,}|\*{3,}|_{3,})\s*$|!\[[^\]]*\]\([^)\s]+\)\s*$)/;
const IMAGE = /^!\[([^\]]*)\]\(([^)\s]+)\)\s*$/;
const RECORD_LINE = /^\[([^\]]*)\]\((\/r\/[0-9a-f-]{36})\)\s*$/;
const CALLOUT = /^>\s?\[!(?:NOTE|INFO|TIP|WARNING)\]\s*$/i;

/** The kinds a file name or an address shows as a picture. */
export const isImage = (b: Pick<Block, 'url' | 'text'>) => /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(b.text) || /\.(png|jpe?g|gif|webp|svg|avif)(\?|$)/i.test(b.url ?? '');

/**
 * A paragraph's line as typed: a «\» before a line that would start another block is the escape, not the text. One rule both ways:
 * a line of «\»s followed by a block's first character gets one more «\» and loses one on reading; any other «\» is the text itself.
 */
const LEAD_ESCAPE = /^\\+[#\-*+>!`\d_]/;
const unescapeLine = (l: string) => (LEAD_ESCAPE.test(l) ? l.slice(1) : l);
/** A paragraph's line written so it is read back as that line, not as the start of another block. */
export const escapeLine = (l: string) => (BLOCK_START.test(l) || LEAD_ESCAPE.test(l) ? `\\${l}` : l);

export function parseBlocks(src: string): Block[] {
  const lines = (src ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out: Block[] = [];
  for (let i = 0; i < lines.length;) {
    const line = lines[i]!;
    const fence = /^```(.*)$/.exec(line);
    if (fence) {
      const code: string[] = [];
      for (i++; i < lines.length && !/^```\s*$/.test(lines[i]!); i++) code.push(lines[i]!.replace(/^\\```/, '```'));
      out.push(newBlock('code', code.join('\n'), fence[1]!.trim() ? { lang: fence[1]!.trim() } : {})); i++; continue;
    }
    if (!line.trim()) { i++; continue; }
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { out.push(newBlock('divider')); i++; continue; }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) { out.push(newBlock(`h${h[1]!.length}` as BlockKind, h[2]!.trimEnd())); i++; continue; }
    const todo = /^\s*[-*+]\s+\[( |x|X)\]\s?(.*)$/.exec(line);
    if (todo) { out.push(newBlock('todo', todo[2]!, { checked: todo[1] !== ' ' })); i++; continue; }
    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line);
    if (bullet) { out.push(newBlock('bullet', bullet[1]!)); i++; continue; }
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (num) { out.push(newBlock('number', num[1]!)); i++; continue; }
    if (/^>/.test(line)) {
      const callout = CALLOUT.test(line);
      if (callout) i++;
      const q: string[] = [];
      for (; i < lines.length && /^>/.test(lines[i]!) && !(q.length && CALLOUT.test(lines[i]!)); i++) q.push(lines[i]!.replace(/^>\s?/, ''));
      out.push(newBlock(callout ? 'callout' : 'quote', q.join('\n'))); continue;
    }
    const img = IMAGE.exec(line);
    if (img) { out.push(newBlock('file', img[1]!, { url: img[2]! })); i++; continue; }
    const rec = RECORD_LINE.exec(line);
    if (rec && (i + 1 >= lines.length || !lines[i + 1]!.trim())) { out.push(newBlock('record', rec[1]!, { recordId: RECORD_HREF.exec(rec[2]!)![1]! })); i++; continue; }
    const para: string[] = [];
    for (; i < lines.length && lines[i]!.trim() && (!para.length || !BLOCK_START.test(lines[i]!)); i++) para.push(unescapeLine(lines[i]!));
    out.push(newBlock('p', para.join('\n')));
  }
  return out;
}

/** One block as markdown lines; `n` — the number of a numbered item in its list. */
function blockMarkdown(b: Block, n: number): string {
  const lines = (prefix: string, rest = prefix) => b.text.split('\n').map((l, i) => (i ? rest : prefix) + l).join('\n');
  switch (b.kind) {
    case 'p': return b.text.split('\n').map(escapeLine).join('\n');
    case 'h1': return `# ${b.text.replace(/\n/g, ' ')}`;
    case 'h2': return `## ${b.text.replace(/\n/g, ' ')}`;
    case 'h3': return `### ${b.text.replace(/\n/g, ' ')}`;
    case 'bullet': return `- ${b.text.replace(/\n/g, ' ')}`;
    case 'number': return `${n}. ${b.text.replace(/\n/g, ' ')}`;
    case 'todo': return `- [${b.checked ? 'x' : ' '}] ${b.text.replace(/\n/g, ' ')}`;
    case 'quote': return lines('> ');
    case 'callout': return `> [!NOTE]\n${lines('> ')}`;
    case 'code': return `\`\`\`${b.lang ?? ''}\n${b.text.replace(/^```/gm, '\\```')}\n\`\`\``;
    case 'divider': return '---';
    case 'file': return `![${b.text.replace(/[[\]\n]/g, '')}](${b.url ?? ''})`;
    case 'record': return `[${b.text.replace(/[[\]\n]/g, '')}](/r/${b.recordId})`;
  }
}

/** Empty paragraphs are not kept: the text is what was written, not where the caret stood. */
export function serializeBlocks(blocks: Block[]): string {
  const out: string[] = [];
  let prev: Block | null = null;
  let n = 0;
  for (const b of blocks) {
    if (b.kind === 'p' && !b.text.trim()) continue;
    n = b.kind === 'number' && prev?.kind === 'number' ? n + 1 : 1;
    if (prev) out.push(sameList(prev, b) ? '\n' : '\n\n');
    out.push(blockMarkdown(b, n));
    prev = b;
  }
  return out.join('');
}

/**
 * The address of a file put into a long text of a record: `/api/files/records/<record>/<slot>/<name in base64url>` — the same key
 * the server keeps the file under, so the editor writes the address before the bytes go (the server takes only a named file).
 */
export function recordFileUrl(recordId: string, slot: string, name: string): string {
  const bytes = new TextEncoder().encode((name.replace(/[\\/\0]/g, '_') || 'file').slice(-100));
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return `/api/files/records/${recordId}/${slot}/${btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
}
